/**
 * Shared helpers for workshop city flow (PDP wizard + state collection).
 *
 * Booking URL is owned here (and the inline boot snippet, which installs the
 * same-pathname history sticky before deferred theme scripts). Canonical query:
 * city, event_id, date, wbw_step, variant. Legacy ?venue= is read, never written.
 */
(function (global) {
  if (global.__WB_CITY_FLOW_CORE_LOADED__) return;
  global.__WB_CITY_FLOW_CORE_LOADED__ = true;

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  function slotBookable(slot) {
    if (!slot) return false;
    var ds =
      slot.display_status != null && slot.display_status !== ''
        ? String(slot.display_status).toLowerCase()
        : '';
    if (ds) {
      if (ds === 'hidden' || ds === 'ended' || ds === 'sold_out') return false;
      if (ds === 'bookable') {
        var capBookable = slot.capacity_remaining;
        if (capBookable != null && capBookable !== '' && Number(capBookable) <= 0) return false;
        return true;
      }
    }
    var st = String(slot.status || '').toLowerCase();
    if (st === 'sold_out' || st === 'cancelled' || st === 'completed' || st === 'draft') return false;
    var cap = slot.capacity_remaining;
    if (cap != null && cap !== '' && Number(cap) <= 0) return false;
    return true;
  }

  function eventGidFromCartLine(item) {
    if (!item || !item.properties) return '';
    var props = item.properties;
    if (typeof props === 'object' && !Array.isArray(props)) {
      return String(props._event_metaobject_gid || props._event_id || props.event_id || '').trim();
    }
    var gid = '';
    for (var i = 0; i < props.length; i++) {
      var row = props[i];
      if (!row) continue;
      var key = row[0];
      if (key === '_event_metaobject_gid' || key === '_event_id' || key === 'event_id') {
        gid = String(row[1] || '').trim();
        break;
      }
    }
    return gid;
  }

  function seatsInCartForEvent(cart, eventGid) {
    if (!cart || !cart.items || !eventGid) return 0;
    var target = String(eventGid);
    var total = 0;
    (cart.items || []).forEach(function (item) {
      if (!item) return;
      if (item.properties && item.properties._workshop_addon === 'true') return;
      var gid = eventGidFromCartLine(item);
      if (gid && gid === target) total += item.quantity || 0;
    });
    return total;
  }

  function fetchCartSeatsForEvent(eventGid) {
    var gid = String(eventGid || '').trim();
    if (!gid) return Promise.resolve(0);
    var rootPath = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
    return fetch(rootPath + 'cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        return seatsInCartForEvent(cart, gid);
      });
  }

  function slotDateKey(slot) {
    if (!slot || !slot.date_key) {
      if (slot && slot.schema_start && String(slot.schema_start).length >= 10) {
        return String(slot.schema_start).slice(0, 10);
      }
      return '';
    }
    return String(slot.date_key).slice(0, 10);
  }

  function formatMoney(cents) {
    if (global.Shopify && typeof global.Shopify.formatMoney === 'function') {
      return global.Shopify.formatMoney(cents);
    }
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  function buildDaysMap(slots, bookableOnly) {
    var map = {};
    (slots || []).forEach(function (s) {
      var dk = slotDateKey(s);
      if (!dk) return;
      if (!map[dk]) map[dk] = { bookable: false, any: true };
      if (!bookableOnly || slotBookable(s)) {
        if (slotBookable(s)) map[dk].bookable = true;
        map[dk].any = true;
      }
    });
    return map;
  }

  function renderCalendarGrid(opts) {
    var root = opts.root;
    var viewMonth = opts.viewMonth;
    var selectedDate = opts.selectedDate || '';
    var daysWithEvents = opts.daysWithEvents || {};
    var sel = opts.selectors || {};
    var dayAttr = sel.dayAttr || 'data-wcf-cal-day';
    var monthLabel = root.querySelector(sel.monthLabel || '[data-wcf-cal-month-label]');
    var dowEl = root.querySelector(sel.dow || '[data-wcf-cal-dow]');
    var grid = root.querySelector(sel.grid || '[data-wcf-cal-grid]');
    if (!grid) return;

    var y = viewMonth.getFullYear();
    var m = viewMonth.getMonth();
    if (monthLabel) monthLabel.textContent = MONTHS[m] + ' ' + y;
    if (dowEl) {
      dowEl.innerHTML = DOW.map(function (d) {
        return '<span class="wcf-cal-dow">' + d + '</span>';
      }).join('');
    }

    var first = new Date(y, m, 1);
    var startPad = first.getDay();
    var daysInMonth = new Date(y, m + 1, 0).getDate();
    var html = '';
    var i;
    for (i = 0; i < startPad; i++) html += '<span class="wcf-cal-day wcf-cal-day--empty"></span>';
    for (var d = 1; d <= daysInMonth; d++) {
      var key = y + '-' + String(m + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
      var info = daysWithEvents[key];
      var cls = 'wcf-cal-day';
      if (!info) cls += ' wcf-cal-day--muted';
      else {
        cls += ' wcf-cal-day--has-event';
        if (!info.bookable) cls += ' wcf-cal-day--soldout-only';
      }
      if (selectedDate === key) cls += ' wcf-cal-day--selected';
      var disabled = !info ? ' disabled' : '';
      html +=
        '<span class="wcf-cal-day-wrap"><button type="button" class="' +
        cls +
        '" ' +
        dayAttr +
        '="' +
        key +
        '"' +
        disabled +
        '>' +
        d +
        '</button></span>';
    }
    grid.innerHTML = html;
  }

  function renderPickableCalendarGrid(opts) {
    var root = opts.root;
    var viewMonth = opts.viewMonth;
    var selectedDate = opts.selectedDate || '';
    var sel = opts.selectors || {};
    var dayAttr = sel.dayAttr || 'data-wcf-wl-cal-day';
    var monthLabel = root.querySelector(sel.monthLabel || '[data-wcf-wl-cal-month-label]');
    var dowEl = root.querySelector(sel.dow || '[data-wcf-wl-cal-dow]');
    var grid = root.querySelector(sel.grid || '[data-wcf-wl-cal-grid]');
    if (!grid) return;
    var y = viewMonth.getFullYear();
    var m = viewMonth.getMonth();
    if (monthLabel) monthLabel.textContent = MONTHS[m] + ' ' + y;
    if (dowEl) {
      dowEl.innerHTML = DOW.map(function (d) {
        return '<span class="wcf-cal-dow">' + d + '</span>';
      }).join('');
    }
    var first = new Date(y, m, 1);
    var startPad = first.getDay();
    var daysInMonth = new Date(y, m + 1, 0).getDate();
    var html = '';
    var i;
    for (i = 0; i < startPad; i++) html += '<span class="wcf-cal-day wcf-cal-day--empty"></span>';
    for (var d = 1; d <= daysInMonth; d++) {
      var key = y + '-' + String(m + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0');
      var cls = 'wcf-cal-day wcf-cal-day--pickable';
      if (selectedDate === key) cls += ' wcf-cal-day--selected';
      html +=
        '<span class="wcf-cal-day-wrap"><button type="button" class="' +
        cls +
        '" ' +
        dayAttr +
        '="' +
        key +
        '">' +
        d +
        '</button></span>';
    }
    grid.innerHTML = html;
  }

  function getCartSections() {
    try {
      if (global.Alpine && global.Alpine.store && global.Alpine.store('xCartHelper')) {
        return global.Alpine.store('xCartHelper').getSectionsToRender().map(function (s) {
          return s.id;
        });
      }
    } catch (_) {}
    return [];
  }

  function dispatchCartUi(body) {
    try {
      if (body && body.sections && global.Alpine && global.Alpine.store && global.Alpine.store('xCartHelper')) {
        global.Alpine.store('xCartHelper').reRenderSections(body.sections);
      }
    } catch (_) {}
    try {
      if (global.Alpine && global.Alpine.store && global.Alpine.store('xCartHelper')) {
        var bubble = document.querySelector('#cart-icon-bubble span');
        global.Alpine.store('xCartHelper').currentItemCount = parseInt((bubble && bubble.innerHTML) || '0', 10);
      }
    } catch (_) {}
    document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
    document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: body } }));
    document.dispatchEvent(new CustomEvent('cart:open'));
    try {
      if (global.Alpine && global.Alpine.store && global.Alpine.store('xMiniCart')) {
        if (!global.Alpine.store('xCartNoti') || !global.Alpine.store('xCartNoti').enable) {
          global.Alpine.store('xMiniCart').openCart();
        }
      }
    } catch (_) {}
  }

  function bootStr(key) {
    try {
      var v = global[key];
      return v != null && String(v).trim() ? String(v).trim() : '';
    } catch (_) {
      return '';
    }
  }

  function normalizeWbwStep(raw) {
    var st = String(raw || '')
      .trim()
      .toLowerCase();
    if (st === 'calendar' || st === 'time') return 'datetime';
    if (st === 'datetime' || st === 'variants') return st;
    return '';
  }

  function parseUrlParams() {
    try {
      var p = new URLSearchParams(global.location.search || '');
      var city = (p.get('city') || p.get('venue') || '').trim() || bootStr('__WB_INITIAL_CITY__');
      var date = (p.get('date') || '').trim().slice(0, 10) || bootStr('__WB_INITIAL_DATE__').slice(0, 10);
      var stepLegacy = (p.get('step') || '').trim().toLowerCase();
      var wbwStep = normalizeWbwStep(p.get('wbw_step') || bootStr('__WB_INITIAL_WBW_STEP__') || stepLegacy);
      return {
        city: city.toLowerCase(),
        date: date,
        step: stepLegacy || wbwStep,
        wbwStep: wbwStep,
        eventId: (p.get('event_id') || '').trim() || bootStr('__WB_INITIAL_EVENT_ID__'),
        variantId: (p.get('variant') || '').trim() || bootStr('__WB_INITIAL_VARIANT__'),
      };
    } catch (_) {
      return {
        city: bootStr('__WB_INITIAL_CITY__').toLowerCase(),
        date: bootStr('__WB_INITIAL_DATE__').slice(0, 10),
        step: normalizeWbwStep(bootStr('__WB_INITIAL_WBW_STEP__')),
        wbwStep: normalizeWbwStep(bootStr('__WB_INITIAL_WBW_STEP__')),
        eventId: bootStr('__WB_INITIAL_EVENT_ID__'),
        variantId: bootStr('__WB_INITIAL_VARIANT__'),
      };
    }
  }

  function hasCityBookingDeepLink() {
    var p = parseUrlParams();
    return !!(p.eventId || p.date || p.wbwStep === 'datetime' || p.wbwStep === 'variants');
  }

  function numericIdForUrl(val) {
    var s = String(val == null ? '' : val).trim();
    if (!s) return '';
    if (s.indexOf('gid://') === 0) {
      var parts = s.split('/');
      return String(parts[parts.length - 1] || '').replace(/\D/g, '');
    }
    if (/^\d+$/.test(s)) return s;
    var digits = s.replace(/\D/g, '');
    return digits.length >= 6 ? digits : '';
  }

  function findSlotByUrlEventId(slotList, eventId) {
    var want = numericIdForUrl(eventId);
    if (!want || !slotList || !slotList.length) return null;
    for (var i = 0; i < slotList.length; i++) {
      var s = slotList[i];
      if (!s) continue;
      var raw = s._raw || {};
      var ids = [
        s.gid,
        s.id,
        s.event_id,
        s.canonical_event_id,
        raw.id,
        raw.gid,
        raw.event_id,
        raw.canonical_event_id,
      ];
      for (var j = 0; j < ids.length; j++) {
        if (numericIdForUrl(ids[j]) === want) return s;
      }
    }
    return null;
  }

  function updateCityBookingUrl(patch) {
    if (!patch || typeof URLSearchParams === 'undefined') return;
    try {
      var params = new URLSearchParams(global.location.search || '');
      if (Object.prototype.hasOwnProperty.call(patch, 'eventId')) {
        var eid = numericIdForUrl(patch.eventId);
        if (eid) {
          global.__WB_STRIP_EVENT_ID_PARAM__ = false;
          params.set('event_id', eid);
          global.__WB_URL_EVENT_ID_NUMERIC__ = eid;
        } else {
          global.__WB_STRIP_EVENT_ID_PARAM__ = true;
          global.__WB_URL_EVENT_ID_NUMERIC__ = '';
          global.__WB_INITIAL_EVENT_ID__ = null;
          params.delete('event_id');
        }
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'variantId')) {
        var vid = numericIdForUrl(patch.variantId);
        if (vid) {
          params.set('variant', vid);
          global.__WB_URL_VARIANT_NUMERIC__ = vid;
        } else {
          params.delete('variant');
          global.__WB_URL_VARIANT_NUMERIC__ = '';
          global.__WB_LOCK_VARIANT_URL__ = false;
        }
      }
      if (
        Object.prototype.hasOwnProperty.call(patch, 'city') ||
        Object.prototype.hasOwnProperty.call(patch, 'venue')
      ) {
        var token = Object.prototype.hasOwnProperty.call(patch, 'city') ? patch.city : patch.venue;
        token = String(token == null ? '' : token).trim();
        if (token) {
          global.__WB_STRIP_CITY_PARAM__ = false;
          global.__WB_STRIP_VENUE_PARAM__ = false;
          params.set('city', token);
          params.delete('venue');
          global.__WB_URL_CITY__ = token;
        } else {
          global.__WB_STRIP_CITY_PARAM__ = true;
          global.__WB_STRIP_VENUE_PARAM__ = true;
          params.delete('city');
          params.delete('venue');
          global.__WB_URL_CITY__ = '';
          global.__WB_INITIAL_CITY__ = null;
        }
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'date')) {
        var d = String(patch.date || '')
          .trim()
          .slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
          global.__WB_STRIP_DATE_PARAM__ = false;
          params.set('date', d);
        } else {
          global.__WB_STRIP_DATE_PARAM__ = true;
          params.delete('date');
          global.__WB_INITIAL_DATE__ = null;
        }
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'step')) {
        var st = normalizeWbwStep(patch.step);
        if (st) {
          global.__WB_STRIP_STEP_PARAM__ = false;
          params.set('wbw_step', st);
        } else {
          global.__WB_STRIP_STEP_PARAM__ = true;
          params.delete('wbw_step');
          global.__WB_INITIAL_WBW_STEP__ = null;
        }
      }
      var qs = params.toString();
      var nextUrl = global.location.pathname + (qs ? '?' + qs : '') + (global.location.hash || '');
      var cur = global.location.pathname + (global.location.search || '') + (global.location.hash || '');
      if (nextUrl === cur) return;
      global.history.replaceState(global.history.state, '', nextUrl);
    } catch (_) {}
  }

  function applyCityWizardRestore(ctrl) {
    if (!ctrl || !ctrl.root || ctrl.root.__wcfUrlRestoreDone) return false;
    var p = parseUrlParams();
    if (!p.eventId && !p.date && p.wbwStep !== 'datetime' && p.wbwStep !== 'variants') {
      return false;
    }
    var state = ctrl.state;
    var slotList = typeof ctrl.getSlots === 'function' ? ctrl.getSlots() : ctrl.slotList || [];
    var slot = p.eventId ? findSlotByUrlEventId(slotList, p.eventId) : null;
    if ((p.eventId || p.date) && !slot && !(slotList && slotList.length)) {
      return false;
    }
    ctrl.root.__wcfUrlRestoreDone = true;
    var dk = '';
    if (slot && typeof ctrl.slotDateKey === 'function') dk = ctrl.slotDateKey(slot) || '';
    if (!dk) dk = p.date || '';
    if (dk) {
      state.selectedDate = dk;
      if (/^\d{4}-\d{2}/.test(dk)) {
        state.viewMonth = new Date(Number(dk.slice(0, 4)), Number(dk.slice(5, 7)) - 1, 1);
      }
    }
    if (slot) state.selectedSlot = slot;
    if (p.variantId) {
      var vid = numericIdForUrl(p.variantId) || String(p.variantId).trim();
      if (typeof ctrl.focusVariant === 'function') ctrl.focusVariant(vid);
      else state.focusedVariantId = vid;
      if (!state.variantQty) state.variantQty = {};
      if (vid && !state.variantQty[vid]) state.variantQty[vid] = 1;
    }
    var bookable = !!(slot && (typeof ctrl.slotBookable !== 'function' || ctrl.slotBookable(slot)));
    var wantVariants = p.wbwStep === 'variants' || (!!p.variantId && p.wbwStep !== 'datetime');
    var didGo = false;
    if (wantVariants && bookable && typeof ctrl.goTo === 'function') {
      var steps = typeof ctrl.stepsByBranch === 'function' ? ctrl.stepsByBranch() : ['calendar', 'variants'];
      var vi = steps.indexOf('variants');
      if (vi >= 0) {
        ctrl.goTo(vi);
        didGo = true;
      } else if (typeof ctrl.render === 'function') ctrl.render();
    } else if (typeof ctrl.render === 'function') {
      ctrl.render();
    }
    if (!didGo && typeof ctrl.syncUrl === 'function' && (slot || dk || p.variantId)) ctrl.syncUrl();
    return true;
  }

  function patchHistoryBookingParams() {
    if (global.__WB_HISTORY_EVENT_PATCHED__) return;
    if (!global.history || typeof global.history.replaceState !== 'function') return;
    global.__WB_HISTORY_EVENT_PATCHED__ = true;

    function stickyBookingParams(absoluteUrl, currentParams) {
      /* Never copy booking query onto another path (sibling PDP, finder, tabs). */
      if (absoluteUrl.pathname !== global.location.pathname) {
        return absoluteUrl.pathname + absoluteUrl.search + absoluteUrl.hash;
      }
      var stripEventId = !!global.__WB_STRIP_EVENT_ID_PARAM__;
      var stickyEventId = stripEventId
        ? ''
        : (currentParams.get('event_id') || '').trim() || String(global.__WB_URL_EVENT_ID_NUMERIC__ || '');
      if (stickyEventId && !(absoluteUrl.searchParams.get('event_id') || '').trim()) {
        absoluteUrl.searchParams.set('event_id', stickyEventId);
      }
      var stickyVariant =
        (currentParams.get('variant') || '').trim() || String(global.__WB_URL_VARIANT_NUMERIC__ || '');
      if (
        stickyVariant &&
        global.__WB_LOCK_VARIANT_URL__ === true &&
        !(absoluteUrl.searchParams.get('variant') || '').trim()
      ) {
        absoluteUrl.searchParams.set('variant', stickyVariant);
      }
      var stripCity = !!global.__WB_STRIP_CITY_PARAM__ || !!global.__WB_STRIP_VENUE_PARAM__;
      var stickyCity = stripCity
        ? ''
        : (currentParams.get('city') || '').trim() ||
          (currentParams.get('venue') || '').trim() ||
          String(global.__WB_URL_CITY__ || '') ||
          bootStr('__WB_INITIAL_CITY__');
      if (stickyCity && !(absoluteUrl.searchParams.get('city') || '').trim()) {
        absoluteUrl.searchParams.set('city', stickyCity);
        absoluteUrl.searchParams.delete('venue');
      }
      var stickyStep =
        global.__WB_STRIP_STEP_PARAM__
          ? ''
          : (currentParams.get('wbw_step') || '').trim() || normalizeWbwStep(bootStr('__WB_INITIAL_WBW_STEP__'));
      if (stickyStep && !(absoluteUrl.searchParams.get('wbw_step') || '').trim()) {
        absoluteUrl.searchParams.set('wbw_step', stickyStep);
      }
      var stickyDate = global.__WB_STRIP_DATE_PARAM__
        ? ''
        : (currentParams.get('date') || '').trim() || bootStr('__WB_INITIAL_DATE__').slice(0, 10);
      if (stickyDate && !(absoluteUrl.searchParams.get('date') || '').trim()) {
        absoluteUrl.searchParams.set('date', stickyDate);
      }
      return absoluteUrl.pathname + absoluteUrl.search + absoluteUrl.hash;
    }

    /* location.href keeps product path for hash-only URLs (#amc-po-faqs); origin would collapse to /. */
    var originalReplaceState = global.history.replaceState;
    global.history.replaceState = function (state, unused, url) {
      try {
        if (url != null && String(url) !== '') {
          var currentParams = new URLSearchParams(global.location.search);
          url = stickyBookingParams(new URL(String(url), global.location.href), currentParams);
        }
      } catch (e) {}
      return originalReplaceState.call(this, state, unused, url);
    };

    var originalPushState = global.history.pushState;
    global.history.pushState = function (state, unused, url) {
      try {
        if (url != null && String(url) !== '') {
          var currentParams = new URLSearchParams(global.location.search);
          url = stickyBookingParams(new URL(String(url), global.location.href), currentParams);
        }
      } catch (e) {}
      return originalPushState.call(this, state, unused, url);
    };
  }

  patchHistoryBookingParams();

  function isWeekendDateKey(dateKey) {
    if (!dateKey) return false;
    var d = new Date(dateKey + 'T12:00:00');
    if (isNaN(d.getTime())) return false;
    var dow = d.getDay();
    return dow === 0 || dow === 6;
  }

  function isWithinNextDays(dateKey, days) {
    if (!dateKey) return false;
    var d = new Date(dateKey + 'T12:00:00');
    if (isNaN(d.getTime())) return false;
    var now = new Date();
    now.setHours(0, 0, 0, 0);
    var end = new Date(now);
    end.setDate(end.getDate() + days);
    return d >= now && d <= end;
  }

  function filterSlots(slots, filters) {
    filters = filters || {};
    return (slots || []).filter(function (s) {
      var dk = slotDateKey(s);
      if (filters.availableOnly && !slotBookable(s)) return false;
      if (filters.thisWeekend && !isWeekendDateKey(dk)) return false;
      if (filters.next7Days && !isWithinNextDays(dk, 7)) return false;
      return true;
    });
  }

  function mergeCityMeta(city, metaByHandle) {
    if (!city || !metaByHandle) return city;
    var m = metaByHandle[city.product_handle];
    if (!m) return city;
    if (!city.featured_image_url && m.featured_image_url) city.featured_image_url = m.featured_image_url;
    if (!city.product_url && m.product_url) city.product_url = m.product_url;
    if (!city.city_name && m.city_name) city.city_name = m.city_name;
    if (!city.venue_name && m.venue_name) city.venue_name = m.venue_name;
    if (m.latitude != null) city.latitude = m.latitude;
    if (m.longitude != null) city.longitude = m.longitude;
    if (!city.title && m.title) city.title = m.title;
    return city;
  }

  function formatWaitlistError(body) {
    if (!body) return '';
    if (Array.isArray(body.errors) && body.errors.length) {
      return body.errors
        .map(function (e) {
          if (!e) return '';
          return e.message ? String(e.message) : String(e.field || '');
        })
        .filter(Boolean)
        .join(' ');
    }
    if (body.message && body.message !== body.error) return String(body.message);
    if (body.description) return String(body.description);
    if (body.error && body.error !== 'VALIDATION_ERROR') return String(body.error);
    return '';
  }

  function normalizeWaitlistSource(source) {
    var s = String(source || '').trim();
    return s || 'no_events';
  }

  function slugifyVenueId(text) {
    return String(text || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function resolveWaitlistVenueId(root, data) {
    if (!root || !root.getAttribute) {
      return String((data && data.productHandle) || '').trim();
    }
    var fromAttr = String(root.getAttribute('data-venue-id') || '').trim();
    if (fromAttr) return fromAttr;
    var city = String(root.getAttribute('data-city-name') || '').trim();
    var state = String(root.getAttribute('data-state-name') || '').trim();
    var fromCityState = slugifyVenueId(city && state ? city + '-' + state : city || state);
    if (fromCityState) return fromCityState;
    var venueName = String(root.getAttribute('data-venue-name') || '').trim();
    var fromVenue = slugifyVenueId(venueName);
    if (fromVenue) return fromVenue;
    return String((data && data.productHandle) || root.getAttribute('data-product-handle') || '').trim();
  }

  function normalizeEventStatus(slot) {
    var st = String((slot && slot.status) || 'active')
      .trim()
      .toLowerCase();
    if (!st) return 'active';
    if (st === 'cancelled') return 'completed';
    if (st === 'started') return 'active';
    return st;
  }

  /**
   * City PDP contact / location stamps for order line properties.
   * Reads data-* from wizard root (product metafields + optional venue / venue_details).
   * Lat/lng intentionally omitted. Blank attrs are skipped (no shop-wide fallbacks).
   */
  function appendVenueContactProperties(root, props) {
    if (!props) props = {};
    if (!root || !root.getAttribute) return props;

    function attr(name) {
      return String(root.getAttribute(name) || '').trim();
    }

    var address = attr('data-venue-address');
    var state = attr('data-state-name');
    var country = attr('data-address-country');
    var phone = attr('data-venue-phone');
    var hours = attr('data-venue-opening-hours');
    var email = attr('data-venue-email');
    var link = attr('data-venue-link');

    if (address) props._venue_address = address;
    if (state) props._display_state = state;
    if (country) props._address_country = country;
    if (phone) props._venue_phone = phone;
    if (hours) props._venue_opening_hours = hours;
    if (email) props._venue_email = email;
    if (link) props._venue_link = link;
    return props;
  }

  /**
   * Cart validation (workshop-cart-validate) reads _event_id, _capacity_remaining, _event_status.
   */
  function appendCartValidationProperties(slot, props) {
    if (!props) props = {};
    if (!slot) return props;

    var gid = String(slot.gid || '').trim();
    if (gid) {
      props._event_id = gid;
      props._event_metaobject_gid = gid;
    }

    if (slot.capacity_remaining != null && slot.capacity_remaining !== '') {
      var cap = Number(slot.capacity_remaining);
      if (Number.isFinite(cap) && cap >= 0) {
        props._capacity_remaining = String(Math.floor(cap));
        props._capacity_source = 'live_state';
      }
    }

    if (slot.display_status != null && slot.display_status !== '') {
      props._display_status = String(slot.display_status);
    }
    if (slot.capacity_as_of != null && slot.capacity_as_of !== '') {
      props._capacity_as_of = String(slot.capacity_as_of);
    }

    props._event_status = normalizeEventStatus(slot);
    return props;
  }

  function resolveWorkshopType(root, data) {
    var fromAttr = root && root.getAttribute ? String(root.getAttribute('data-workshop-type') || '').trim().toLowerCase() : '';
    if (fromAttr === 'mosaic' || fromAttr === 'ceramic' || fromAttr === 'resin' || fromAttr === 'flower') {
      return fromAttr;
    }
    var handle = String((data && data.productHandle) || (root && root.getAttribute('data-product-handle')) || '').toLowerCase();
    var title = root && root.getAttribute ? String(root.getAttribute('data-product-title') || '').toLowerCase() : '';
    if (handle.indexOf('ceramic') >= 0 || title.indexOf('ceramic') >= 0) return 'ceramic';
    if (handle.indexOf('resin') >= 0 || title.indexOf('resin') >= 0) return 'resin';
    if (handle.indexOf('flower') >= 0 || handle.indexOf('floral') >= 0 || title.indexOf('flower') >= 0 || title.indexOf('floral') >= 0) {
      return 'flower';
    }
    if (handle.indexOf('mosaic') >= 0 || title.indexOf('mosaic') >= 0) return 'mosaic';
    return 'mosaic';
  }

  function submitWaitlist(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors',
    }).then(function (res) {
      return res.json().then(function (body) {
        return { ok: res.ok, status: res.status, body: body || {} };
      }).catch(function () {
        return res.text().then(function (txt) {
          return { ok: res.ok, status: res.status, body: { description: txt } };
        });
      });
    }).then(function (r) {
      if (!r.ok) {
        var msg = formatWaitlistError(r.body) || 'Request failed (' + r.status + ')';
        throw new Error(msg);
      }
      if (r.body && r.body.success === false) {
        throw new Error(formatWaitlistError(r.body) || 'Could not add to waitlist.');
      }
      return r.body;
    });
  }

  function syncSessionsLoadErrorUi(root, apiError) {
    if (!root) return;
    var notice = root.querySelector('[data-wcf-step="wl-notice"]');
    if (!notice) return;
    var titleEl = notice.querySelector('.wcf-step__title');
    var subEl = notice.querySelector('.wcf-step__sub');
    var form = notice.querySelector('.wcf-wl-form-wrap');
    var submit = notice.querySelector('[data-wcf-action="wl-submit"]');
    var retry = notice.querySelector('[data-wcf-action="retry-slots"]');

    if (!notice.getAttribute('data-wcf-wl-title-default') && titleEl) {
      notice.setAttribute('data-wcf-wl-title-default', titleEl.textContent || '');
    }
    if (!notice.getAttribute('data-wcf-wl-sub-default') && subEl) {
      notice.setAttribute('data-wcf-wl-sub-default', subEl.textContent || '');
    }

    var isError = !!apiError;
    notice.classList.toggle('wcf-step--load-error', isError);
    if (form) form.hidden = isError;
    if (submit) submit.hidden = isError;
    if (retry) retry.hidden = !isError;

    if (isError) {
      if (titleEl) titleEl.textContent = 'Workshop sessions could not be loaded';
      if (subEl) {
        subEl.textContent =
          'Sessions may still be available. Tap Try again to load dates.';
      }
      console.warn('[workshop-city-flow] sessions load error — retry, not waitlist', {
        error: String(apiError),
      });
      return;
    }

    if (titleEl) titleEl.textContent = notice.getAttribute('data-wcf-wl-title-default') || '';
    if (subEl) subEl.textContent = notice.getAttribute('data-wcf-wl-sub-default') || '';
  }

  var VARIANT_RAIL_GAP = 20;
  var VARIANT_RAIL_SLOP = 8;

  function variantRailPitch(wrap) {
    var card = wrap && wrap.querySelector ? wrap.querySelector('.wcf-variant-row') : null;
    var w = card ? card.getBoundingClientRect().width : 0;
    return (w || 112) + VARIANT_RAIL_GAP;
  }

  function clampVariantRailScroll(wrap, v) {
    var max = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
    return Math.max(0, Math.min(max, v));
  }

  function snapVariantRailScroll(wrap, left) {
    var pitch = variantRailPitch(wrap);
    if (pitch <= 1) return clampVariantRailScroll(wrap, left);
    return clampVariantRailScroll(wrap, Math.round(left / pitch) * pitch);
  }

  /** Desktop mouse drag: follow the pointer, then ease to a card. Clicks stay on the card. */
  function bindVariantRailMotion(wrap) {
    if (!wrap || wrap.__pcwRailMotionBound) return;
    wrap.__pcwRailMotionBound = true;

    var motion = { raf: 0, from: 0, to: 0, start: 0, dur: 0 };
    var drag = { on: false, moved: false, listening: false, id: 0, x: 0, y: 0, left: 0, samples: [] };
    var moveRaf = 0;
    var moveX = 0;

    function suspendSnap() {
      wrap.style.setProperty('scroll-snap-type', 'none', 'important');
    }

    function restoreSnap() {
      wrap.style.removeProperty('scroll-snap-type');
    }

    function stopMotion() {
      if (motion.raf) {
        cancelAnimationFrame(motion.raf);
        motion.raf = 0;
      }
    }

    function goTo(target, dur) {
      target = clampVariantRailScroll(wrap, target);
      stopMotion();
      suspendSnap();
      motion.from = wrap.scrollLeft;
      motion.to = target;
      motion.start = performance.now();
      motion.dur = dur || 280;
      if (Math.abs(motion.to - motion.from) < 0.5) {
        wrap.scrollLeft = motion.to;
        restoreSnap();
        return;
      }
      function tick(now) {
        var t = Math.min(1, (now - motion.start) / motion.dur);
        wrap.scrollLeft = motion.from + (motion.to - motion.from) * (1 - Math.pow(1 - t, 3));
        if (t < 1) {
          motion.raf = requestAnimationFrame(tick);
          return;
        }
        wrap.scrollLeft = motion.to;
        motion.raf = 0;
        restoreSnap();
      }
      motion.raf = requestAnimationFrame(tick);
    }

    function stepBy(dir, dur) {
      var pitch = variantRailPitch(wrap);
      var from = motion.raf ? motion.to : wrap.scrollLeft;
      goTo((Math.round(from / pitch) + (dir > 0 ? 1 : -1)) * pitch, dur);
    }

    wrap.__pcwGoTo = goTo;
    wrap.__pcwStepBy = stepBy;

    function applyDragLeft() {
      wrap.scrollLeft = drag.left - (moveX - drag.x);
    }

    function unbindDoc() {
      if (!drag.listening) return;
      drag.listening = false;
      document.removeEventListener('pointermove', onDocMove, true);
      document.removeEventListener('pointerup', endDrag, true);
      document.removeEventListener('pointercancel', endDrag, true);
    }

    function bindDoc() {
      if (drag.listening) return;
      drag.listening = true;
      document.addEventListener('pointermove', onDocMove, true);
      document.addEventListener('pointerup', endDrag, true);
      document.addEventListener('pointercancel', endDrag, true);
    }

    function endDrag(e) {
      if (!drag.on) return;
      if (e && e.pointerId !== drag.id) return;
      drag.on = false;
      wrap.classList.remove('is-dragging');
      if (moveRaf) {
        cancelAnimationFrame(moveRaf);
        moveRaf = 0;
      }
      unbindDoc();
      try {
        wrap.releasePointerCapture(drag.id);
      } catch (_) {}
      if (!drag.moved) {
        wrap.__pcwSuppressClick = false;
        return;
      }
      applyDragLeft();
      var vx = 0;
      var now = performance.now();
      var recent = [];
      var i;
      for (i = 0; i < drag.samples.length; i++) {
        if (now - drag.samples[i].t <= 80) recent.push(drag.samples[i]);
      }
      if (recent.length < 2 && drag.samples.length >= 2) recent = drag.samples.slice(-2);
      if (recent.length >= 2) {
        var dt = Math.max(16, recent[recent.length - 1].t - recent[0].t);
        vx = (recent[recent.length - 1].x - recent[0].x) / dt;
      }
      if (vx > 1.1) vx = 1.1;
      if (vx < -1.1) vx = -1.1;
      var nearest = snapVariantRailScroll(wrap, wrap.scrollLeft);
      var target = nearest;
      if (Math.abs(vx) >= 0.55) {
        target = clampVariantRailScroll(
          wrap,
          nearest + variantRailPitch(wrap) * (vx > 0 ? -1 : 1)
        );
      }
      goTo(target, 280);
      window.setTimeout(function () {
        wrap.__pcwSuppressClick = false;
      }, 120);
    }

    function onDocMove(e) {
      if (!drag.on || e.pointerId !== drag.id) return;
      var dx = e.clientX - drag.x;
      var dy = e.clientY - drag.y;
      if (!drag.moved) {
        if (Math.abs(dx) < VARIANT_RAIL_SLOP && Math.abs(dy) < VARIANT_RAIL_SLOP) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          drag.on = false;
          unbindDoc();
          wrap.__pcwSuppressClick = false;
          return;
        }
        drag.moved = true;
        wrap.__pcwSuppressClick = true;
        wrap.classList.add('is-dragging');
        stopMotion();
        suspendSnap();
        try {
          wrap.setPointerCapture(e.pointerId);
        } catch (_) {}
      }
      moveX = e.clientX;
      drag.samples.push({ x: e.clientX, t: performance.now() });
      while (drag.samples.length > 5) drag.samples.shift();
      if (!moveRaf) {
        moveRaf = requestAnimationFrame(function () {
          moveRaf = 0;
          if (!drag.on || !drag.moved) return;
          applyDragLeft();
        });
      }
    }

    wrap.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      if (e.button && e.button !== 0) return;
      if (
        e.target.closest(
          '[data-wcf-qty-inc], [data-wcf-qty-dec], .wcf-variant-qty-col, .wcf-qty--variant, .pcw-variants-nav'
        )
      ) {
        return;
      }
      drag.on = true;
      drag.moved = false;
      drag.id = e.pointerId;
      drag.x = e.clientX;
      drag.y = e.clientY;
      drag.left = wrap.scrollLeft;
      drag.samples = [{ x: e.clientX, t: performance.now() }];
      moveX = e.clientX;
      wrap.__pcwSuppressClick = false;
      bindDoc();
    });
    wrap.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });
  }

  global.WorkshopCityFlow = {
    MONTHS: MONTHS,
    DOW: DOW,
    slotBookable: slotBookable,
    slotDateKey: slotDateKey,
    formatMoney: formatMoney,
    buildDaysMap: buildDaysMap,
    renderCalendarGrid: renderCalendarGrid,
    renderPickableCalendarGrid: renderPickableCalendarGrid,
    getCartSections: getCartSections,
    dispatchCartUi: dispatchCartUi,
    parseUrlParams: parseUrlParams,
    hasCityBookingDeepLink: hasCityBookingDeepLink,
    updateCityBookingUrl: updateCityBookingUrl,
    applyCityWizardRestore: applyCityWizardRestore,
    findSlotByUrlEventId: findSlotByUrlEventId,
    filterSlots: filterSlots,
    mergeCityMeta: mergeCityMeta,
    isWeekendDateKey: isWeekendDateKey,
    formatWaitlistError: formatWaitlistError,
    normalizeWaitlistSource: normalizeWaitlistSource,
    resolveWaitlistVenueId: resolveWaitlistVenueId,
    resolveWorkshopType: resolveWorkshopType,
    appendCartValidationProperties: appendCartValidationProperties,
    appendVenueContactProperties: appendVenueContactProperties,
    eventGidFromCartLine: eventGidFromCartLine,
    seatsInCartForEvent: seatsInCartForEvent,
    fetchCartSeatsForEvent: fetchCartSeatsForEvent,
    submitWaitlist: submitWaitlist,
    syncSessionsLoadErrorUi: syncSessionsLoadErrorUi,
    bindVariantRailMotion: bindVariantRailMotion,
  };
})(typeof window !== 'undefined' ? window : this);
